import { EventBus } from '../core/EventBus';

// Temporary buff that expires after duration
export interface TemporaryBuff {
  id: string;
  type: 'damage' | 'firerate' | 'speed' | 'shield';
  multiplier: number;
  duration: number;
  remainingTime: number;
  startTime: number;
}

// Permanent upgrade levels (0-5)
export interface PermanentUpgrades {
  fireRateLevel: number;
  magazineSizeLevel: number;
  projectileSpeedLevel: number;
}

// Which weapons have been unlocked
export interface WeaponUnlocks {
  railgun: boolean;
  plasmaCannon: boolean;
  homingMissiles: boolean;
}

// Buff colors for UI
export const BUFF_COLORS: Record<string, number> = {
  damage: 0xffa500,    // Orange
  firerate: 0xffcc00,  // Gold
  speed: 0xff6600,     // Red-orange
  shield: 0x00ffff,    // Cyan
};

export class WeaponUpgradeManager {
  private activeBuffs: TemporaryBuff[] = [];
  private permanentUpgrades: PermanentUpgrades = {
    fireRateLevel: 0,
    magazineSizeLevel: 0,
    projectileSpeedLevel: 0,
  };
  private unlockedWeapons: WeaponUnlocks = {
    railgun: false,
    plasmaCannon: false,
    homingMissiles: false,
  };

  // Buff duration ranges (seconds)
  private readonly BUFF_DURATIONS = {
    damage: { min: 30, max: 60 },
    firerate: { min: 20, max: 45 },
    speed: { min: 15, max: 30 },
    shield: { min: 20, max: 40 },
  };

  // Upgrade bonuses per level
  private readonly UPGRADE_BONUSES = {
    fireRate: 0.10,        // +10% per level
    magazineSize: 0.20,    // +20% per level
    projectileSpeed: 0.15, // +15% per level
  };

  private readonly MAX_LEVEL = 5;

  constructor() {
    // Listen for upgrade pickup events
    EventBus.on('upgrade:buff', (data: { type: 'damage' | 'firerate' | 'speed' | 'shield' }) => {
      this.addTemporaryBuff(data.type);
    });

    EventBus.on('upgrade:permanent', (data: { type: keyof PermanentUpgrades }) => {
      this.applyPermanentUpgrade(data.type);
    });

    EventBus.on('upgrade:weapon', (data: { weapon: keyof WeaponUnlocks }) => {
      this.unlockWeapon(data.weapon);
    });
  }

  public update(_dt: number): void {
    const currentTime = performance.now();

    // Update all active buffs
    for (let i = this.activeBuffs.length - 1; i >= 0; i--) {
      const buff = this.activeBuffs[i];
      buff.remainingTime = buff.duration - (currentTime - buff.startTime) / 1000;

      if (buff.remainingTime <= 0) {
        this.removeBuff(i);
      }
    }
  }

  public addTemporaryBuff(type: TemporaryBuff['type']): void {
    const durations = this.BUFF_DURATIONS[type];
    const duration = durations.min + Math.random() * (durations.max - durations.min);

    // Check if same buff type already exists - refresh it
    const existing = this.activeBuffs.find(b => b.type === type);
    if (existing) {
      // Extend duration
      existing.startTime = performance.now();
      existing.duration = Math.max(existing.remainingTime, duration);
      existing.remainingTime = existing.duration;

      EventBus.emit('buff:refreshed', {
        type,
        duration: existing.duration,
        color: BUFF_COLORS[type],
      });
    } else {
      // Add new buff
      const buff: TemporaryBuff = {
        id: `buff_${Date.now()}_${type}`,
        type,
        multiplier: 1.5, // +50%
        duration,
        remainingTime: duration,
        startTime: performance.now(),
      };

      this.activeBuffs.push(buff);

      EventBus.emit('buff:activated', {
        type,
        duration,
        color: BUFF_COLORS[type],
      });
    }
  }

  private removeBuff(index: number): void {
    const buff = this.activeBuffs[index];
    this.activeBuffs.splice(index, 1);

    EventBus.emit('buff:expired', {
      type: buff.type,
    });
  }

  public applyPermanentUpgrade(type: keyof PermanentUpgrades): void {
    if (this.permanentUpgrades[type] >= this.MAX_LEVEL) {
      // Already maxed
      EventBus.emit('upgrade:maxed', { type });
      return;
    }

    this.permanentUpgrades[type]++;

    EventBus.emit('upgrade:applied', {
      type,
      newLevel: this.permanentUpgrades[type],
      maxLevel: this.MAX_LEVEL,
    });
  }

  public unlockWeapon(weapon: keyof WeaponUnlocks): void {
    if (this.unlockedWeapons[weapon]) {
      // Already unlocked - give bonus points instead
      EventBus.emit('upgrade:duplicate', { weapon, bonusPoints: 500 });
      return;
    }

    this.unlockedWeapons[weapon] = true;

    EventBus.emit('weapon:unlocked', {
      weapon,
    });
  }

  // === Multiplier Getters (called by weapons) ===

  public getDamageMultiplier(): number {
    let multiplier = 1.0;

    // Check for damage buff
    const damageBuff = this.activeBuffs.find(b => b.type === 'damage');
    if (damageBuff) {
      multiplier *= damageBuff.multiplier;
    }

    return multiplier;
  }

  public getFireRateMultiplier(): number {
    let multiplier = 1.0;

    // Check for fire rate buff
    const fireRateBuff = this.activeBuffs.find(b => b.type === 'firerate');
    if (fireRateBuff) {
      multiplier *= fireRateBuff.multiplier;
    }

    // Add permanent upgrade bonus
    multiplier += this.permanentUpgrades.fireRateLevel * this.UPGRADE_BONUSES.fireRate;

    return multiplier;
  }

  public getSpeedMultiplier(): number {
    // For bike speed buff
    const speedBuff = this.activeBuffs.find(b => b.type === 'speed');
    if (speedBuff) {
      return speedBuff.multiplier;
    }
    return 1.0;
  }

  public getShieldMultiplier(): number {
    // For damage reduction buff - returns 0.5 when active (50% damage reduction)
    const shieldBuff = this.activeBuffs.find(b => b.type === 'shield');
    if (shieldBuff) {
      return 0.5; // Take 50% less damage
    }
    return 1.0;
  }

  public getMagazineSizeMultiplier(): number {
    return 1.0 + this.permanentUpgrades.magazineSizeLevel * this.UPGRADE_BONUSES.magazineSize;
  }

  public getProjectileSpeedMultiplier(): number {
    return 1.0 + this.permanentUpgrades.projectileSpeedLevel * this.UPGRADE_BONUSES.projectileSpeed;
  }

  // === Status Getters ===

  public getActiveBuffs(): TemporaryBuff[] {
    return [...this.activeBuffs];
  }

  public isWeaponUnlocked(weapon: keyof WeaponUnlocks): boolean {
    return this.unlockedWeapons[weapon];
  }

  public getPermanentUpgrades(): PermanentUpgrades {
    return { ...this.permanentUpgrades };
  }

  public getUpgradeLevel(type: keyof PermanentUpgrades): number {
    return this.permanentUpgrades[type];
  }

  public getMaxLevel(): number {
    return this.MAX_LEVEL;
  }

  // For serialization/save games
  public getState(): {
    permanentUpgrades: PermanentUpgrades;
    unlockedWeapons: WeaponUnlocks;
  } {
    return {
      permanentUpgrades: { ...this.permanentUpgrades },
      unlockedWeapons: { ...this.unlockedWeapons },
    };
  }

  public loadState(state: {
    permanentUpgrades?: Partial<PermanentUpgrades>;
    unlockedWeapons?: Partial<WeaponUnlocks>;
  }): void {
    if (state.permanentUpgrades) {
      Object.assign(this.permanentUpgrades, state.permanentUpgrades);
    }
    if (state.unlockedWeapons) {
      Object.assign(this.unlockedWeapons, state.unlockedWeapons);
    }
  }
}
